import {
  useContext,
  useEffect,
  useRef,
  useState,
  type ComponentProps,
  type CSSProperties
} from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Formik, type FormikHelpers } from 'formik';
import {
  Button,
  Form,
  Row,
  Col,
  Spinner
} from 'react-bootstrap';
import CrawlSettingsDrawer from '../history/CrawlSettingsDrawer';
import {
  useGetDomainCrawlSettingsQuery,
  useStartCrawlingMutation,
  useStopCrawlingMutation
} from '@/app/Crawl/api/mainCrawlApi';
import { object, string } from 'yup';
import SocketProgressBar from './ProgressBar';
import { useGetUserInfoQuery } from '@/app/common/api/authApi';
import { errorMessage, warningMessage } from '@/utils/toast';
import { NewCrawlContext } from '@/context/Context';
import CancelModal from '@/app/common/components/CancelModal';
import ChildrensTooltip from '@/components/common/ChildrensTooltip';
import useDomain from '@/hooks/useDomain';
import classNames from 'classnames';
import { useGetTariffInfoQuery } from '@/app/KeywordTracker/api/keywordsApi';
import UrlScopeDropdown, {
  buildStartCrawlSettings,
  includePatternsFromSettings,
  inferUrlScope,
  type UrlScope
} from './UrlScopeDropdown';
import NewFeatureDot from '@/components/common/NewFeatureDot';
import useSidebarFeatureIndicator, {
  CRAWL_SETTINGS_FEATURE_KEY
} from '@/hooks/useSidebarFeatureIndicator';

interface NewCrawlFormProps {
  lastCrawlStatus?: number | null;
  oldJobPage?: string;
  crawlStoped?: boolean;
}

interface CrawlFormValues {
  start_url: string;
}

interface StartCrawlResponse {
  code: number;
  data: string | { job_page: string };
}

type CrawlSettingsDrawerConfig = ComponentProps<
  typeof CrawlSettingsDrawer
>['config'];

/*کامپوننت برای خزش جدید در ابزار خزش که شامل شروع  توقف و نمایش پراگرس خزش میشود*/
const NewCrawlForm = ({
  lastCrawlStatus,
  oldJobPage,
  crawlStoped
}: NewCrawlFormProps): React.JSX.Element => {
  const buttonOverlay = useRef<HTMLButtonElement | null>(null);
  const [openDrawer, setOpenDrawer] = useState(false);
  const { markAsRead, isUnread } = useSidebarFeatureIndicator();
  const showSettingsNewDot = isUnread(CRAWL_SETTINGS_FEATURE_KEY);
  const { domain_slug, domain } = useDomain();
  useGetUserInfoQuery();
  const domainRef = useRef<HTMLInputElement | null>(null);

  const [startCrawling] = useStartCrawlingMutation();
  const [stopCrawling] = useStopCrawlingMutation();

  const {
    isSubmitted,
    config,
    jobPage,
    setConfig,
    setIsSubmitted,
    setJobPage,
    canceled,
    setCanceled
  } = useContext(NewCrawlContext);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [urlScope, setUrlScope] = useState<UrlScope>('host');
  const [scopeTouched, setScopeTouched] = useState(false);
  const { data: savedCrawlSettings } = useGetDomainCrawlSettingsQuery(
    domain_slug,
    { skip: !domain_slug }
  );

  useEffect(() => {
    if (scopeTouched) return;
    const saved = savedCrawlSettings?.data?.settings;
    if (saved && typeof saved === 'object') {
      setUrlScope(inferUrlScope(saved as Record<string, unknown>));
    }
  }, [savedCrawlSettings, scopeTouched]);

  const handleUrlScopeChange = (scope: UrlScope, startUrl: string) => {
    setUrlScope(scope);
    setScopeTouched(true);
    if (openDrawer) return;
    const settings = buildStartCrawlSettings(scope, startUrl);
    setConfig({
      ...config,
      url_scope: scope,
      domain_scope: settings.domain_scope,
      include_urls_is_enable: settings.include_urls_is_enable,
      include_urls_value: includePatternsFromSettings(settings)
    });
  };
  /* 
    زمانی که خزش در حال انجام هست و کاربر صفحه بارگذاری میکنه 
    آخرین JobPage ست میشه 
  */
  useEffect(() => {
    if (lastCrawlStatus === 5 && oldJobPage) setJobPage(oldJobPage);
  }, [lastCrawlStatus, oldJobPage, setJobPage]);

  /* برای لغو خزش */
  const onCancel = async (): Promise<void> => {
    setCanceled(true);
    await stopCrawling({
      job_page: jobPage,
      domain_slug
    })
      .unwrap()
      .then(() => {
        // console.log('');
      })
      .catch(() => {
        errorMessage('در توقف خزش خطایی رخ داده است');
        setCanceled(false);
      });
  };

  /* منطق شروع خزش */
  const onSubmit = async (
    values: CrawlFormValues,
    actions: FormikHelpers<CrawlFormValues>
  ): Promise<void> => {
    setIsSubmitted(true);
    setCanceled(false);
    try {
      const response = (await startCrawling({
        start_url: values.start_url,
        domain_slug,
        settings: buildStartCrawlSettings(urlScope, values.start_url)
      }).unwrap()) as StartCrawlResponse;
      actions.resetForm();
      if (response.code !== 200) {
        warningMessage(response.data);
        setIsSubmitted(false);
      } else {
        setJobPage((response.data as { job_page: string }).job_page);
      }
    } catch (error) {
      console.log(error);
    }
  };
  const { data: trackerInfo, isFetching: isFetchingTrackerInfo } =
    useGetTariffInfoQuery(domain_slug);
  const isAccessAllowed = () =>
    trackerInfo?.data?.access_level !== 3 && !isFetchingTrackerInfo;

  return (
    <>
      <Formik<CrawlFormValues>
        onSubmit={onSubmit}
        initialValues={{
          start_url: ''
        }}
        // اعتبار سنجی پیوند وارد شده برای خزش
        validationSchema={object().shape({
          start_url: string()
            .required('وارد کردن دامنه الزامی می‌باشد')
            .url('دامنه معتبر نیست (باید با http یا https شروع بشه).')
            .matches(
              new RegExp(domain.replace('.', '\\.'), 'i'),
              ` دامنه باید شامل ${domain} باشد`
            )
        })}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit
        }) => (
          <div className={` mt-3 mb-4`}>
            <Form onSubmit={handleSubmit}>
              <Row
                className={
                  !lastCrawlStatus
                    ? lastCrawlStatus === 0
                      ? ''
                      : 'justify-content-md-center'
                    : ''
                }
              >
                <Col md={8}>
                  {(isSubmitted && jobPage) ||
                  (lastCrawlStatus == 5 && jobPage) ? (
                    <div className="d-flex flex-column flex-md-row gap-2 align-items-stretch">
                      <div className="flex-1">
                        <SocketProgressBar />
                      </div>
                      {/* پاپ آپ برای توقف خزش وتایید آن */}
                      {/* <OverlayTrigger
                        trigger="click"
                        rootClose
                        placement="bottom"
                        overlay={
                          <Popover
                            id="popover-basic"
                            style={{
                              width: '350px',
                              backgroundColor: 'var(--falcon-tertiary-bg)'
                            }}
                          >
                            <Popover.Body className="p-0 text-800">
                              <div className="p-3">
                                آیا مطمن هستید میخواید خزش را برای دامنه{' '}
                                {domain} متوقف کنید؟
                              </div>
                              <Divider className="my-0 p-0" />
                              <div className="d-flex align-items-center gap-2 px-2 pb-2">
                                <Button
                                  size="sm"
                                  onClick={() => {
                                    onCancel();
                                    buttonOverlay.current.click();
                                  }}
                                  variant="danger"
                                >
                                  بله متوقف بشه
                                </Button>
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  onClick={() => buttonOverlay.current.click()}
                                >
                                  نه
                                </Button>
                              </div>
                            </Popover.Body>
                          </Popover>
                        }
                      > */}
                      {/* دکمه توقف خزش */}
                      <Button
                        ref={buttonOverlay}
                        disabled={canceled || crawlStoped}
                        variant="danger"
                        className="fs--1"
                        onClick={() => setCancelModalOpen(true)}
                      >
                        {!canceled && !crawlStoped ? (
                          <span>توقف و مشاهده</span>
                        ) : (
                          <div className="d-flex align-items-center gap-1">
                            <Spinner
                              style={{
                                width: '20px',
                                marginBottom: '-3px',
                                paddingLeft: '3px',
                                height: '20px'
                              }}
                              className="me-2"
                            />
                            <span>لطفا منتظر بمانید :)</span>
                          </div>
                        )}
                      </Button>
                      {/* </OverlayTrigger> */}
                    </div>
                  ) : (
                    <div className="d-flex gap-2 position-relative flex-lg-row flex-column align-items-center">
                      <div
                        className={classNames('input-group input-group-md', {
                          // 'input-group input-group-md': isLgUp,
                          // 'd-flex flex-column gap-2': !isLgUp
                        })}
                      >
                        <UrlScopeDropdown
                          domain={domain}
                          value={urlScope}
                          onChange={scope =>
                            handleUrlScopeChange(scope, values.start_url)
                          }
                        />
                        <Form.Control
                          type="text"
                          isInvalid={Boolean(
                            touched.start_url && errors.start_url
                          )}
                          isValid={Boolean(
                            touched.start_url && !errors.start_url
                          )}
                          ref={domainRef}
                          style={{
                            direction: domainRef?.current?.value?.length
                              ? 'ltr'
                              : 'rtl'
                          }}
                          placeholder={`نشانی شامل ${domain} به همراه https یا http باشه`}
                          value={values.start_url}
                          onChange={event => {
                            handleChange(event);
                            if (
                              urlScope !== 'url' &&
                              urlScope !== 'path'
                            ) {
                              return;
                            }
                            const settings = buildStartCrawlSettings(
                              urlScope,
                              event.target.value
                            );
                            if (openDrawer) return;
                            setConfig({
                              ...config,
                              url_scope: urlScope,
                              domain_scope: 'host',
                              include_urls_is_enable: true,
                              include_urls_value:
                                includePatternsFromSettings(settings)
                            });
                          }}
                          onBlur={handleBlur}
                          name="start_url"
                        />

                        {/* دکمه بازکردن تنظیمات خزش */}
                        <Button
                          style={
                            {
                              borderRadius: '5px 0 0 5px',
                              borderColor: 'var(--falcon-gray-300)',
                              '--falcon-btn-border-color':
                                'var(--falcon-gray-300)',
                              '--falcon-btn-hover-border-color':
                                'var(--falcon-gray-300)',
                              '--falcon-btn-active-border-color':
                                'var(--falcon-gray-300)',
                              '--falcon-btn-disabled-border-color':
                                'var(--falcon-gray-300)'
                            } as CSSProperties
                          }
                          onClick={() => {
                            setOpenDrawer(true);
                            markAsRead(CRAWL_SETTINGS_FEATURE_KEY);
                          }}
                          variant="outline-secondary"
                          title="تنظیمات خزش"
                        >
                          <span className="d-inline-flex align-items-center gap-1">
                            <FontAwesomeIcon icon="cog" />
                            {showSettingsNewDot && <NewFeatureDot />}
                          </span>
                        </Button>
                        {/* تنظیمات خزش */}
                        <CrawlSettingsDrawer
                          setConfig={setConfig}
                          config={config as unknown as CrawlSettingsDrawerConfig}
                          isOpen={openDrawer}
                          setIsOpen={setOpenDrawer}
                          domain={domain}
                          startUrl={values.start_url}
                          urlScope={urlScope}
                          onUrlScopeChange={scope =>
                            handleUrlScopeChange(scope, values.start_url)
                          }
                        />
                      </div>
                      <ChildrensTooltip
                        placement="top"
                        className="w-100 w-lg-auto"
                        html={
                          !isAccessAllowed()
                            ? 'شما دسترسی لازم را ندارید'
                            : // : values.start_url.length === 0
                              // ? 'ادرس رو ابتدا وارد کن'
                              ''
                        }
                      >
                        <Button
                          variant="falcon-primary"
                          type="submit"
                          className="w-100 text-nowrap"
                          disabled={
                            !isAccessAllowed() || isSubmitted
                            // || values.start_url.length === 0 ||
                            // !_.isEqual(errors, {})
                          }
                        >
                          <span>خزش جدید</span>
                        </Button>
                      </ChildrensTooltip>
                      <div
                        style={{
                          position: 'absolute',
                          bottom: '-20px',
                          right: 0
                        }}
                      >
                        <Form.Control.Feedback
                          className="d-block flex-1 m-0 ms-2"
                          type={errors.start_url ? 'invalid' : 'valid'}
                        >
                          {errors.start_url}&nbsp;
                        </Form.Control.Feedback>
                      </div>
                    </div>
                  )}
                </Col>
              </Row>
            </Form>
          </div>
        )}
      </Formik>
      <CancelModal
        setShow={setCancelModalOpen}
        show={cancelModalOpen}
        submitFunc={onCancel}
        submitTitle="آره، متوقف بشه"
        title="توقف خزش"
        desc={`آیا مطمئن هستید میخواید خزش را برای دامنه ${domain} متوقف کنید؟`}
      />
    </>
  );
};

export default NewCrawlForm;
